import { getString } from "common/ui_string_labels/ui-strings-utils";
import { SubmitOrderItem } from "@amzn/ito-client";
import {
    NonStandardHardwareFormat,
    nonStandardHardwareUniqueIdentifier,
    getUseNonStandardOrderOutput,
    useNonstandardOrderProps,
    useNonStandardOrderOutput,
    NonStandardIds,
    NonStandardRequiredParamsMap,
} from "interfaces";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { nonStandardOrderNameGenerator } from "helpers/hardware-restock";
import { CatalogicalService } from "services/catalogical-service";
import { CATALOGICAL_ENDPOINT } from "configuration/config";

export const getStringPath: string = "common.component.LocalItOrderConfirmationContent";
const getStringPathCard: string = `${getStringPath}.nonStandardOrder`;
const getErrorStringPath: string = "errors.ito.nonstandard";
const itemNamePath: string = "itemName";

/**
 * Represent the minimum number of orders before disabling the remove button
 */
export const MINIMUM_NUMBER_OF_ORDERS: number = 1;

/**
 * Constant representing the default quantity allowed
 */
const DEFAULT_QUANTITY: number = 0;

/**
 * Constant representing the minimum quantity allowed per order
 */
const MINIMUM_QUANTITY_ALLOWED: number = 1;

/** Represents the maximum number of non standard items per request */
export const MAX_ITEMS = 30;

/** Catalogical service to retrieve the non standard orders id's */
const catalogicalService: CatalogicalService = new CatalogicalService(CATALOGICAL_ENDPOINT);

/**
 * Function that generates a unique non standard error id for a form section
 *
 * @param {string} id - The id of the non standard item
 * @param {keyof NonStandardRequiredParamsMap} key - The form section to generate the error id
 *
 * @return {string} the error id
 */
export const generateNonStandardErrorId = (id: string, key: keyof NonStandardRequiredParamsMap): string => {
    return `${NonStandardIds[key]}-${id}`;
};

/**
 * Formats non-standard orders for ITO submission by mapping them to SubmitOrderItem format
 * Assign them a retrieved non standard order id and generates a custom name.
 *
 * @param {NonStandardHardwareFormat[]} nonStandardOrder - Array of non-standard hardware items to format
 *
 * @return {Promise<Record<string, SubmitOrderItem>>} - Promise resolving to mapped order items with taxonomy IDs as keys
 */
export const formatNonStandardOrderForITO = async (
    nonStandardOrder: NonStandardHardwareFormat[],
): Promise<Record<string, SubmitOrderItem>> => {
    let mappedNonStandardOrder: Record<string, SubmitOrderItem> = {};

    const nonStandardTaxonomies = await catalogicalService.getNonStandardOrdersId(nonStandardOrder.length);

    if (nonStandardTaxonomies.length < nonStandardOrder.length) {
        throw new Error(
            getString("notEnoughTaxonomyIds", getErrorStringPath, {
                length: nonStandardTaxonomies.length,
            }),
        );
    }

    nonStandardOrder.forEach((item, position) => {
        const taxonomyId = nonStandardTaxonomies[position];
        if (!taxonomyId) {
            throw new Error(
                getString("missingTaxonomyId", getErrorStringPath, {
                    position: position,
                }),
            );
        }

        mappedNonStandardOrder[taxonomyId] = {
            quantity: item.quantity,
            details: {
                description: item.description,
                link: item.link,
                name: nonStandardOrderNameGenerator(item.manufacturer, item.sku),
                manufacturer: item.manufacturer,
                sku: item.sku,
            },
        };
    });

    return mappedNonStandardOrder;
};

/**
 * Custom hook for that returns the required parameters to manage the useNonStandardOrder hook
 *
 * @returns {getUseNonStandardOrderOutput} - An object containing the non standard items and a function to update them
 */
export const getUseNonStandardOrder = (): getUseNonStandardOrderOutput => {
    const [nonStandardOrder, setNonStandardOrder] = useState<NonStandardHardwareFormat[]>([]);

    return {
        nonStandardOrder,
        setNonStandardOrder,
    };
};

/**
 * Custom hook for managing the non standard order confirmation
 *
 * @param {NonStandardHardwareFormat[]} nonStandardOrder - The non standard orders
 * @param {React.Dispatch<React.SetStateAction<NonStandardHardwareFormat[]>>} setNonStandardOrder - The to update the non standard orders
 *
 * @returns {useNonStandardOrderOutput} - An object containing the non standard order catalog and auxiliary functions
 */
const useNonStandardOrder = ({
    nonStandardOrder,
    setNonStandardOrder,
}: useNonstandardOrderProps): useNonStandardOrderOutput => {
    const trackBy: string = nonStandardHardwareUniqueIdentifier;

    useEffect(() => {
        if (nonStandardOrder.length === 0) {
            addNonStandardHardware();
        }
    }, []);

    /**
     * Function that updates a partial of a NonStandardHardwareFormat order
     *
     * @param {string} id - The id of the hardware to update
     * @param {Partial<NonStandardHardwareFormat>} updates - The updates to make to the item
     *
     * @returns {void}
     */
    const updateNonStandardHardware = (id: string, updates: Partial<NonStandardHardwareFormat>): void => {
        setNonStandardOrder((prev) => prev.map((item) => (item.id === id ? { ...item, ...updates } : item)));
    };

    /**
     * Function that removes an item from the catalog
     *
     * @param {string} id - The id of the hardware to remove
     *
     * @returns {void}
     */
    const removeItem = (id: string): void => {
        setNonStandardOrder((prev) => prev.filter((item) => item.id !== id));
    };

    /**
     * Auxiliary function that gets the position of an item in the catalog
     *
     * @param {string} id - The id of the hardware to retrieve the position
     *
     * @returns {number} the position of the item in the catalog
     */
    const getItemPosition = (id: string): number => nonStandardOrder.findIndex((item) => item.id === id);

    /**
     * Function that retrieves the name of an item based on its position
     *
     * @param {string} id - The id of the hardware item to retrieve
     *
     * @returns {string} the name of the hardware item
     */
    const getItemName = (id: string): string =>
        getString(itemNamePath, getStringPathCard, { position: getItemPosition(id) + 1 });

    /**
     * Function that adds an empty non standard item to the catalog
     *
     * @returns {void}
     */
    const addNonStandardHardware = (): void => {
        if (nonStandardOrder.length >= MAX_ITEMS) return;

        const newItem: NonStandardHardwareFormat = {
            id: uuidv4(),
            manufacturer: "",
            sku: "",
            link: "",
            description: "",
            quantity: MINIMUM_QUANTITY_ALLOWED,
        };

        setNonStandardOrder((prev) => [...prev, newItem]);
    };

    /**
     * Function that returns if items can be removed
     *
     * @returns {boolean} flag representing if items can be removed
     */
    const canRemoveItems = (): boolean => nonStandardOrder.length > MINIMUM_NUMBER_OF_ORDERS;

    /**
     * Function that ensures a quantity value is not below the minimum allowed
     *
     * @param {number} quantity - The quantity value to validate
     * @returns {number} - The validated quantity, either the input value or minimum allowed
     */
    const enforceMinimumQuantity = (quantity: number): number => {
        if (!quantity) return DEFAULT_QUANTITY;

        return quantity < MINIMUM_QUANTITY_ALLOWED ? MINIMUM_QUANTITY_ALLOWED : quantity;
    };

    /**
     * Function that determines if the current quantity selected is a valid quantity
     *
     * @param {number} quantity - The quantity value to validate
     *
     * @return {boolean} - True if quantity meets minimum requirements, false otherwise
     */
    const isValidQuantity = (quantity: number) => quantity >= MINIMUM_QUANTITY_ALLOWED;

    return {
        catalog: nonStandardOrder,
        trackBy,
        updateNonStandardHardware,
        removeItem,
        getItemName,
        addNonStandardHardware,
        canAddMore: nonStandardOrder.length < MAX_ITEMS,
        canRemoveItems,
        enforceMinimumQuantity,
        isValidQuantity,
    };
};

export default useNonStandardOrder;
